import { useState, useEffect, useCallback, useRef } from "react";
import Footer from "../../components/feature/Footer";
import PlayerProfileModal from "./components/PlayerProfileModal";
import { formatCompact } from "../../utils/format";

const tabs = ["Overall", "Kills", "Playtime", "Money", "Shards", "XP"];
const PAGE_SIZE = 10;
const STATS_API_BASES = [
  "https://stats.applesmp.us/stats",
];
const XP_API = "https://stats.applesmp.us/xp";
const LUCKPERMS_API = "https://luckperms.applesmp.us/groups";
const SERVER_STATUS_API = "https://api.mcsrvstat.us/3/applesmp.us";

interface ServerStatus {
  online: boolean;
  players?: { online: number; max: number };
}

export interface PlayerRow {
  rank: number;
  username: string;
  score: number;
  xp: number;
  shards: number;
  kills: number;
  deaths: number;
  mobsKilled: number;
  moneySpent: number;
  moneyMade: number;
  team: string;
  killstreak: number;
  highestKillstreak: number;
  playtime: string;
  playtimeMs: number;
  online: boolean;
  luckpermsGroup: string;
  luckpermsColor: string | null;
  badge: string | null;
  joinDate?: string;
  lastSeen?: string;
  playtimeHistory?: { label: string; hours: number }[];
}

// â”€â”€â”€ Minecraft color map â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const MC_COLORS: Record<string, { textClass: string; bgClass: string }> = {
  dark_red:     { textClass: "text-red-600",     bgClass: "bg-red-600/10"     },
  red:          { textClass: "text-red-500",     bgClass: "bg-red-500/10"     },
  dark_purple:  { textClass: "text-purple-600",  bgClass: "bg-purple-600/10"  },
  light_purple: { textClass: "text-purple-400",  bgClass: "bg-purple-400/10"  },
  blue:         { textClass: "text-sky-400",     bgClass: "bg-sky-400/10"     },
  dark_blue:    { textClass: "text-sky-700",     bgClass: "bg-sky-700/10"     },
  dark_aqua:    { textClass: "text-cyan-600",    bgClass: "bg-cyan-600/10"    },
  aqua:         { textClass: "text-cyan-400",    bgClass: "bg-cyan-400/10"    },
  dark_green:   { textClass: "text-green-600",   bgClass: "bg-green-600/10"   },
  green:        { textClass: "text-green-400",   bgClass: "bg-green-400/10"   },
  yellow:       { textClass: "text-yellow-400",  bgClass: "bg-yellow-400/10"  },
  gold:         { textClass: "text-amber-500",   bgClass: "bg-amber-500/10"   },
  dark_gray:    { textClass: "text-gray-500",    bgClass: "bg-gray-500/10"    },
  gray:         { textClass: "text-gray-400",    bgClass: "bg-gray-400/10"    },
  white:        { textClass: "text-slate-200",   bgClass: "bg-slate-200/10"   },
};

export const getLuckPermsStyle = (group: string, mcColor: string | null) => {
  const label = group ? group.charAt(0).toUpperCase() + group.slice(1) : "Member";
  const raw = (mcColor ?? "").trim();

  const hexMatch = raw.match(/#?([0-9a-fA-F]{6})/);
  if (hexMatch) {
    return { label, textClass: "", bgClass: "", hexColor: `#${hexMatch[1]}` };
  }

  const codeChar = raw.replace(/^[Â§&]/, "").toLowerCase();
  const CODE_MAP: Record<string, string> = {
    "0": "black", "1": "dark_blue", "2": "dark_green", "3": "dark_aqua",
    "4": "dark_red", "5": "dark_purple", "6": "gold", "7": "gray",
    "8": "dark_gray", "9": "blue", a: "green", b: "aqua",
    c: "red", d: "light_purple", e: "yellow", f: "white",
  };
  const resolvedKey = CODE_MAP[codeChar] ?? raw.toLowerCase();
  const style = MC_COLORS[resolvedKey] ?? { textClass: "text-muted-foreground", bgClass: "bg-muted/20" };
  return { label, ...style, hexColor: undefined };
};

// â”€â”€â”€ Helpers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
export const extractPlayerName = (nameField: unknown): string => {
  const raw =
    typeof nameField === "string"
      ? nameField
      : typeof nameField === "object" &&
        nameField !== null &&
        "v" in (nameField as object)
      ? String((nameField as { v: unknown }).v)
      : String(nameField ?? "");

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(raw, "text/html");
    const text = doc.body.textContent?.trim();
    if (text) return text;
  } catch {
    // fallback below
  }
  return raw.replace(/<[^>]+>/g, "").trim();
};

// Extract balance from list endpoint (supports {d,v} wrapper or plain string)
const extractBalance = (val: unknown): number => {
  if (val === null || val === undefined) return 0;
  if (typeof val === "object") {
    const obj = val as Record<string, unknown>;
    const raw = obj.v ?? obj.d;
    if (raw === "-" || raw === "-" || raw === null || raw === undefined) return 0;
    return parseFloat(String(raw)) || 0;
  }
  if (typeof val === "string") {
    if (val === "-" || val === "-") return 0;
    return parseFloat(val) || 0;
  }
  return 0;
};

// Extract registered/lastSeen date from {d,v} wrapper
const extractDate = (val: unknown): string | undefined => {
  if (!val) return undefined;
  if (typeof val === "object") {
    const obj = val as Record<string, unknown>;
    return obj.d ? String(obj.d) : undefined;
  }
  return String(val);
};

// Extract primary group string from {d,v} wrapper or plain string
const extractGroup = (val: unknown): string => {
  if (!val) return "default";
  if (typeof val === "object") {
    const obj = val as Record<string, unknown>;
    const v = String(obj.v ?? obj.d ?? "default");
    return v === "-" ? "default" : v;
  }
  const s = String(val);
  return s === "-" ? "default" : s;
};

// â”€â”€â”€ Legacy exports (used by PlayerProfileModal) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
// extractPAPI now reads plain string values from the extensions array.
// value is a plain string on each entry - NOT wrapped in {v,d}.
export const extractPAPI = (data: Record<string, unknown>, key: string): number => {
  const extensions = data?.extensions;
  if (!Array.isArray(extensions)) return 0;
  for (const ext of extensions as unknown[]) {
    const extData = (ext as Record<string, unknown>)?.extensionData;
    if (!Array.isArray(extData)) continue;
    for (const section of extData as unknown[]) {
      const tabs = (section as Record<string, unknown>)?.tabs;
      if (!Array.isArray(tabs)) continue;
      for (const tab of tabs as unknown[]) {
        const values = (tab as Record<string, unknown>)?.values;
        if (!Array.isArray(values)) continue;
        for (const entry of values as unknown[]) {
          const ve = entry as Record<string, unknown>;
          const desc = ve?.description as Record<string, unknown> | undefined;
          const name = String(desc?.name ?? ve?.name ?? "");
          if (name === key || name.includes(key)) {
            const n = parseFloat(String(ve?.value ?? ""));
            return isNaN(n) ? 0 : n;
          }
        }
      }
    }
  }
  return 0;
};

export const extractPAPIString = (data: Record<string, unknown>, key: string): string => {
  const extensions = data?.extensions;
  if (!Array.isArray(extensions)) return "";
  for (const ext of extensions as unknown[]) {
    const extData = (ext as Record<string, unknown>)?.extensionData;
    if (!Array.isArray(extData)) continue;
    for (const section of extData as unknown[]) {
      const tabs = (section as Record<string, unknown>)?.tabs;
      if (!Array.isArray(tabs)) continue;
      for (const tab of tabs as unknown[]) {
        const values = (tab as Record<string, unknown>)?.values;
        if (!Array.isArray(values)) continue;
        for (const entry of values as unknown[]) {
          const ve = entry as Record<string, unknown>;
          const desc = ve?.description as Record<string, unknown> | undefined;
          const name = String(desc?.name ?? ve?.name ?? "");
          if (name === key || name.includes(key)) {
            return String(ve?.value ?? "").replace(/<[^>]+>/g, "").trim();
          }
        }
      }
    }
  }
  return "";
};

// â”€â”€â”€ Native Plan field extractors (always present, no PAPI needed) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

/**
 * Kills: from kill_data.player_kills_total (always present in /v1/player response)
 */
const extractKills = (d: Record<string, unknown>): number => {
  const kd = d?.kill_data as Record<string, unknown> | undefined;
  return typeof kd?.player_kills_total === "number" ? kd.player_kills_total : 0;
};

/**
 * Deaths: from kill_data.deaths_total (always present)
 */
const extractDeaths = (d: Record<string, unknown>): number => {
  const kd = d?.kill_data as Record<string, unknown> | undefined;
  return typeof kd?.deaths_total === "number" ? kd.deaths_total : 0;
};

/**
 * Active playtime ms: from info.active_playtime (always present)
 */
const extractPlaytimeMs = (d: Record<string, unknown>): number => {
  const info = d?.info as Record<string, unknown> | undefined;
  return typeof info?.active_playtime === "number" ? info.active_playtime : 0;
};

/**
 * Online status: from info.online (always present)
 */
const extractOnline = (d: Record<string, unknown>): boolean => {
  const info = d?.info as Record<string, unknown> | undefined;
  return typeof info?.online === "boolean" ? info.online : false;
};

/**
 * Money: prefer PAPI extension economy_money, fall back to Vault Economy extension balance.
 * Both come from extensions[].extensionData[].tabs[].values[].
 * value is a plain string (not wrapped in {v,d}).
 */
const extractMoney = (d: Record<string, unknown>): number => {
  const extensions = d?.extensions;
  if (!Array.isArray(extensions)) return 0;

  // Try PlaceholderAPI first (economy_money), then Vault Economy (balance)
  const targets = ["economy_money", "balance"];

  for (const ext of extensions as unknown[]) {
    const extData = (ext as Record<string, unknown>)?.extensionData;
    if (!Array.isArray(extData)) continue;
    for (const section of extData as unknown[]) {
      const tabs = (section as Record<string, unknown>)?.tabs;
      if (!Array.isArray(tabs)) continue;
      for (const tab of tabs as unknown[]) {
        const values = (tab as Record<string, unknown>)?.values;
        if (!Array.isArray(values)) continue;
        for (const entry of values as unknown[]) {
          const ve = entry as Record<string, unknown>;
          const desc = ve?.description as Record<string, unknown> | undefined;
          const name = String(desc?.name ?? ve?.name ?? "");
          if (targets.some((t) => name === t)) {
            const raw = String(ve?.value ?? "").trim();
            if (raw && raw !== "-" && raw !== "-") {
              const n = parseFloat(raw);
              if (!isNaN(n)) return n;
            }
          }
        }
      }
    }
  }
  return 0;
};

/**
 * LuckPerms group + color from extensions.
 * Reads primaryGroup STRING value and prefix COMPONENT value for color.
 */
const extractLuckPerms = (d: Record<string, unknown>): { group: string; color: string | null } => {
  const extensions = d?.extensions;
  if (!Array.isArray(extensions)) return { group: "default", color: null };

  let group = "default";
  let color: string | null = null;

  for (const ext of extensions as unknown[]) {
    const pluginName = String(
      ((ext as Record<string, unknown>)?.extensionInformation as Record<string, unknown>)?.pluginName ?? ""
    );
    if (!pluginName.toLowerCase().includes("luckperms")) continue;

    const extData = (ext as Record<string, unknown>)?.extensionData;
    if (!Array.isArray(extData)) continue;

    for (const section of extData as unknown[]) {
      const tabs = (section as Record<string, unknown>)?.tabs;
      if (!Array.isArray(tabs)) continue;
      for (const tab of tabs as unknown[]) {
        const values = (tab as Record<string, unknown>)?.values;
        if (!Array.isArray(values)) continue;
        for (const entry of values as unknown[]) {
          const ve = entry as Record<string, unknown>;
          const desc = ve?.description as Record<string, unknown> | undefined;
          const name = String(desc?.name ?? ve?.name ?? "");
          const value = String(ve?.value ?? "");

          if (name === "primaryGroup" || name === "permissionGroups") {
            if (value && value !== "-") group = value;
          }

          // Extract color from prefix COMPONENT JSON
          if (name === "prefix" && value) {
            try {
              const parsed = JSON.parse(value);
              // Look for color in extra[] or top-level
              const extras = parsed?.extra ?? [];
              for (const extra of extras) {
                if (extra?.color && extra.color !== "white") {
                  color = extra.color;
                  break;
                }
              }
              if (!color && parsed?.color && parsed.color !== "white") {
                color = parsed.color;
              }
            } catch {
              // not JSON, skip
            }
          }
        }
      }
    }
  }

  return { group, color };
};

// â”€â”€â”€ Formatting â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const formatPlaytime = (ms: number): string => {
  if (!ms || ms <= 0) return "0h";
  const hours = Math.round(ms / (1000 * 60 * 60));
  return `${hours}h`;
};

const rankBadge = (rank: number) => {
  if (rank === 1) return "ri-medal-line text-yellow-400";
  if (rank === 2) return "ri-medal-line text-slate-300";
  if (rank === 3) return "ri-medal-line text-amber-600";
  return null;
};

const formatTimestamp = (date: Date) =>
  date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });

const formatLeaderboardMoney = (value: number): string => {
  const amount = Number(value || 0);
  return `$${Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits: amount >= 1_000_000 ? 2 : 1,
  }).format(amount)}`;
};

interface AppleStatsRow {
  uuid?: string | null;
  username: string;
  money?: number;
  balance?: number;
  shards?: number;
  nicestShards?: string | number;
  playtime?: string;
  kills?: number;
  playerKills?: number;
  deaths?: number;
  blocksPlaced?: number;
  blocks_placed?: number;
  blocksBroken?: number;
  blocks_broken?: number;
  mobsKilled?: number;
  mobs_killed?: number;
  moneySpent?: number;
  money_spent?: number;
  moneyMade?: number;
  money_made?: number;
  team?: string;
  killstreak?: number;
  killStreak?: number;
  highestKillstreak?: number;
  highest_killstreak?: number;
  online?: boolean;
  lastSeen?: string;
  last_seen?: string;
  updatedAt?: string;
  updated_at?: string;
}

interface XpRow {
  discordId?: string;
  username?: string | null;
  xp?: number;
  earned?: number;
  spent?: number;
  updatedAt?: number | string | null;
}

interface LuckPermsGroupsResponse {
  primary_group?: string;
  group?: string;
  groups?: string[];
}

const parsePlaytimeMs = (value: unknown): number => {
  if (typeof value === "number") return value;
  const text = String(value ?? "").trim().toLowerCase();
  if (!text) return 0;
  let totalMs = 0;
  const dayMatch = text.match(/(\d+(?:\.\d+)?)\s*d/);
  const hourMatch = text.match(/(\d+(?:\.\d+)?)\s*h/);
  const minuteMatch = text.match(/(\d+(?:\.\d+)?)\s*m/);
  if (dayMatch) totalMs += Number(dayMatch[1]) * 24 * 60 * 60 * 1000;
  if (hourMatch) totalMs += Number(hourMatch[1]) * 60 * 60 * 1000;
  if (minuteMatch) totalMs += Number(minuteMatch[1]) * 60 * 1000;
  return totalMs;
};

const parseApiNumber = (value: unknown): number => {
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  const text = String(value ?? "").trim().toLowerCase().replace(/,/g, "");
  if (!text) return 0;
  const multiplier = text.endsWith("k") ? 1_000
    : text.endsWith("m") ? 1_000_000
    : text.endsWith("b") ? 1_000_000_000
    : text.endsWith("t") ? 1_000_000_000_000
    : 1;
  const parsed = Number(text.replace(/[^0-9.-]/g, ""));
  return Number.isFinite(parsed) ? parsed * multiplier : 0;
};

const fetchGroupsForPlayers = async (players: AppleStatsRow[]) => {
  const entries = await Promise.all(
    players.map(async (player) => {
      try {
        const res = await fetch(`${LUCKPERMS_API}/${encodeURIComponent(player.username)}`, {
          signal: AbortSignal.timeout(8000),
        });
        if (!res.ok) throw new Error("LuckPerms request failed");
        const data = (await res.json()) as LuckPermsGroupsResponse;
        const groups = Array.isArray(data.groups) && data.groups.length
          ? data.groups
          : [data.primary_group || data.group || "default"];
        return [player.username.toLowerCase(), groups] as const;
      } catch {
        return [player.username.toLowerCase(), ["default"]] as const;
      }
    })
  );
  return new Map(entries);
};

const fetchStatsTop = async (): Promise<unknown> => {
  let lastError: unknown = null;
  for (const base of STATS_API_BASES) {
    try {
      const response = await fetch(`${base}/top`, { signal: AbortSignal.timeout(10000), cache: "no-store" });
      if (!response.ok) throw new Error(`${base} returned ${response.status}`);
      return response.json();
    } catch (error) {
      lastError = error;
      console.error(`Leaderboard API failed for ${base}:`, error);
    }
  }
  throw lastError || new Error("Stats API unavailable");
};

const fetchXpTop = async (): Promise<Map<string, XpRow>> => {
  try {
    const response = await fetch(`${XP_API}/top`, { signal: AbortSignal.timeout(10000), cache: "no-store" });
    if (!response.ok) throw new Error(`XP API returned ${response.status}`);
    const rows = (await response.json()) as XpRow[];
    return new Map(
      rows
        .filter((row) => row.username)
        .map((row) => [String(row.username).toLowerCase(), row])
    );
  } catch (error) {
    console.error("XP API failed:", error);
    return new Map();
  }
};

const toPlayerRow = (player: AppleStatsRow, index: number, groups: string[], xpRow?: XpRow): PlayerRow => {
  const playtimeMs = parsePlaytimeMs(player.playtime);
  return {
    rank: index + 1,
    username: player.username,
    score: parseApiNumber(player.money ?? player.balance),
    xp: parseApiNumber(xpRow?.xp),
    shards: parseApiNumber(player.shards ?? player.nicestShards),
    kills: parseApiNumber(player.kills ?? player.playerKills),
    deaths: parseApiNumber(player.deaths),
    mobsKilled: parseApiNumber(player.mobsKilled ?? player.mobs_killed),
    moneySpent: parseApiNumber(player.moneySpent ?? player.money_spent),
    moneyMade: parseApiNumber(player.moneyMade ?? player.money_made),
    team: player.team || "None",
    killstreak: parseApiNumber(player.killstreak ?? player.killStreak),
    highestKillstreak: parseApiNumber(player.highestKillstreak ?? player.highest_killstreak),
    playtime: player.playtime || formatPlaytime(playtimeMs),
    playtimeMs,
    online: Boolean(player.online),
    luckpermsGroup: groups.filter(Boolean).join(", ") || "default",
    luckpermsColor: null,
    badge: null,
    lastSeen: player.lastSeen || player.last_seen || player.updatedAt || player.updated_at,
  };
};

// â”€â”€â”€ Main Component â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
export default function LeaderboardPage() {
  const [activeTab, setActiveTab] = useState("Overall");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [serverStatus, setServerStatus] = useState<ServerStatus | null>(null);
  const [players, setPlayers] = useState<PlayerRow[]>([]);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [dataSource, setDataSource] = useState<"live" | "error">("error");
  const [refreshing, setRefreshing] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState<PlayerRow | null>(null);
  const playersRef = useRef<PlayerRow[]>(players);
  useEffect(() => { playersRef.current = players; }, [players]);

  useEffect(() => { setPage(1); }, [search, activeTab]);

  const fetchPlayerData = useCallback(async () => {
    setRefreshing(true);
    try {
      const [statsJson, xpByName] = await Promise.all([
        fetchStatsTop(),
        fetchXpTop()
      ]);
      const rawRows = Array.isArray(statsJson)
        ? statsJson
        : Array.isArray(statsJson?.players)
          ? statsJson.players
          : Array.isArray(statsJson?.data)
            ? statsJson.data
            : Array.isArray(statsJson?.leaderboard)
              ? statsJson.leaderboard
              : [];
      const liveRows = Array.isArray(rawRows)
        ? (rawRows as AppleStatsRow[]).filter((row) => row?.username)
        : [];
      if (!liveRows.length) throw new Error("No leaderboard data returned");

      const groupsByName = await fetchGroupsForPlayers(liveRows);
      const built = liveRows.map((row, index) =>
        toPlayerRow(row, index, groupsByName.get(row.username.toLowerCase()) ?? ["default"], xpByName.get(row.username.toLowerCase()))
      );

      const maxMoney = Math.max(...built.map((p) => p.score), 1);
      const maxPlaytime = Math.max(...built.map((p) => p.playtimeMs), 1);
      const maxShards = Math.max(...built.map((p) => p.shards || 0), 1);
      const overallScore = (p: PlayerRow) => {
        const kd = Math.min(p.kills / Math.max(p.deaths, 1), 10) / 10;
        const moneyNorm = p.score / maxMoney;
        const ptNorm = p.playtimeMs / maxPlaytime;
        const shardsNorm = (p.shards || 0) / maxShards;
        return kd * 0.35 + moneyNorm * 0.3 + ptNorm * 0.2 + shardsNorm * 0.15;
      };

      const sorted = built
        .sort((a, b) => overallScore(b) - overallScore(a))
        .map((p, i) => ({ ...p, rank: i + 1 }));

      setPlayers(sorted);
      setDataSource("live");
    } catch (error) {
      console.error("Leaderboard API failed:", error);
      setPlayers([]);
      setDataSource("error");
    }

    setLastUpdated(new Date());
    setRefreshing(false);
  }, []);

  useEffect(() => {
    fetchPlayerData();
    const interval = window.setInterval(fetchPlayerData, 30000);
    return () => window.clearInterval(interval);
  }, [fetchPlayerData]);

  // Server status (online/player count)
  useEffect(() => {
    const fetchServerStatus = () => {
      fetch(SERVER_STATUS_API, { signal: AbortSignal.timeout(5000), cache: "no-store" })
        .then((r) => r.json())
        .then((data: ServerStatus) => setServerStatus(data))
        .catch(() => setServerStatus({ online: false }));
    };
    fetchServerStatus();
    const interval = window.setInterval(fetchServerStatus, 30000);
    return () => window.clearInterval(interval);
  }, []);

  const playersOnline = serverStatus?.players?.online ?? null;
  const maxPlayers    = serverStatus?.players?.max ?? 10000;
  const isOnline      = serverStatus?.online ?? null;

  const calcOverallScore = (player: PlayerRow, allPlayers: PlayerRow[]): number => {
    const maxMoney    = Math.max(...allPlayers.map((p) => p.score), 1);
    const maxPlaytime = Math.max(...allPlayers.map((p) => p.playtimeMs), 1);
    const maxShards   = Math.max(...allPlayers.map((p) => p.shards || 0), 1);
    const kd          = player.kills / Math.max(player.deaths, 1);
    const kdCapped    = Math.min(kd, 10);
    const kdNorm      = kdCapped / 10;
    const moneyNorm   = player.score / maxMoney;
    const playtimeNorm = player.playtimeMs / maxPlaytime;
    const shardsNorm  = (player.shards || 0) / maxShards;
    return kdNorm * 0.35 + moneyNorm * 0.3 + playtimeNorm * 0.2 + shardsNorm * 0.15;
  };

  const sortedPlayers = [...players]
    .sort((a, b) => {
      if (activeTab === "Kills")    return b.kills - a.kills;
      if (activeTab === "Playtime") return b.playtimeMs - a.playtimeMs;
      if (activeTab === "Money")    return b.score - a.score;
      if (activeTab === "Shards")   return (b.shards || 0) - (a.shards || 0);
      if (activeTab === "XP")       return (b.xp || 0) - (a.xp || 0);
      return calcOverallScore(b, players) - calcOverallScore(a, players);
    })
    .map((p, i) => ({ ...p, rank: i + 1 }));

  const filtered = sortedPlayers.filter((p) =>
    p.username.toLowerCase().includes(search.toLowerCase())
  );

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated  = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const topThree = sortedPlayers.slice(0, 3);

  return (
    <div className="flex min-h-screen flex-col bg-background bg-[radial-gradient(circle_at_top_left,rgba(255,77,94,0.16),transparent_32%),radial-gradient(circle_at_80%_20%,rgba(34,211,238,0.10),transparent_28%)]">
      <main className="flex-1 max-w-6xl mx-auto w-full px-4 py-10 sm:px-6 lg:px-8">

        {/* Hero */}
        <section className="mb-8 overflow-hidden rounded-3xl border border-border bg-card/80 shadow-2xl shadow-black/20 backdrop-blur">
          <div className="grid gap-0 lg:grid-cols-[1.15fr_0.85fr]">
            <div className="p-6 sm:p-8">
              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-xs font-bold uppercase tracking-wide text-primary">
                <i className="ri-trophy-line" />
                Live Leaderboard
              </div>
              <h1 className="max-w-2xl text-4xl font-black tracking-tight text-foreground sm:text-5xl">
                AppleSMP player rankings
              </h1>
              <p className="mt-4 max-w-xl text-sm leading-6 text-muted-foreground sm:text-base">
                Track the top players by overall performance, kills, playtime, and economy balance. Click any player row for deeper stats.
              </p>

              <div className="mt-7 grid gap-3 sm:grid-cols-3">
                <div className="rounded-2xl border border-border bg-background/70 p-4">
                  <div className="mb-3 flex h-9 w-9 items-center justify-center rounded-xl bg-green-500/10 text-green-400"><i className="ri-wifi-line" /></div>
                  <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Status</p>
                  <p className={`mt-1 text-lg font-black ${isOnline ? "text-green-400" : isOnline === false ? "text-red-400" : "text-muted-foreground"}`}>{isOnline === null ? "Checking" : isOnline ? "Online" : "Offline"}</p>
                </div>
                <div className="rounded-2xl border border-border bg-background/70 p-4">
                  <div className="mb-3 flex h-9 w-9 items-center justify-center rounded-xl bg-sky-500/10 text-sky-300"><i className="ri-group-line" /></div>
                  <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Players</p>
                  <p className="mt-1 text-lg font-black">{isOnline === null ? "-" : isOnline && playersOnline !== null ? `${playersOnline.toLocaleString()} / ${maxPlayers.toLocaleString()}` : `0 / ${maxPlayers.toLocaleString()}`}</p>
                </div>
                <div className="rounded-2xl border border-border bg-background/70 p-4">
                  <div className="mb-3 flex h-9 w-9 items-center justify-center rounded-xl bg-amber-500/10 text-amber-300"><i className="ri-database-2-line" /></div>
                  <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Data</p>
                  <p className={`mt-1 text-lg font-black ${dataSource === "live" ? "text-green-400" : "text-red-400"}`}>{dataSource === "live" ? "Live" : "API Error"}</p>
                </div>
              </div>
            </div>

            <div className="border-t border-border bg-background/50 p-6 sm:p-8 lg:border-l lg:border-t-0">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Current Podium</p>
                  <p className="text-sm text-muted-foreground">Overall score leaders</p>
                </div>
                <button onClick={fetchPlayerData} disabled={refreshing} className="flex h-9 w-9 items-center justify-center rounded-xl border border-border bg-card text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50" title="Refresh leaderboard">
                  <i className={`ri-refresh-line ${refreshing ? "animate-spin" : ""}`} />
                </button>
              </div>
              <div className="grid gap-3">
                {topThree.map((player) => (
                  <button key={player.username} onClick={() => setSelectedPlayer(player)} className="flex items-center gap-3 rounded-2xl border border-border bg-card p-3 text-left transition-colors hover:bg-accent/70">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 font-black text-primary">#{player.rank}</div>
                    <img src={`https://mc-heads.net/avatar/${player.username}/40`} alt={player.username} className="h-10 w-10 rounded-xl bg-primary/10 object-cover" onError={(e) => { (e.currentTarget as HTMLImageElement).src = "https://mc-heads.net/avatar/MHF_Steve/40"; }} />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-black text-foreground">{player.username}</p>
                      <p className="text-xs text-muted-foreground">{formatLeaderboardMoney(player.score)} - {formatCompact(player.kills)} kills</p>
                    </div>
                    <i className="ri-arrow-right-s-line text-muted-foreground" />
                  </button>
                ))}
              </div>
              {lastUpdated && <p className="mt-4 text-right text-[11px] font-mono text-muted-foreground/60">Updated {formatTimestamp(lastUpdated)}</p>}
            </div>
          </div>
        </section>
        {/* Controls */}
        <div className="mb-5 flex flex-col gap-3 rounded-2xl border border-border bg-card/80 p-3 shadow-xl shadow-black/10 sm:flex-row sm:items-center">
          <div className="flex items-center gap-1 overflow-x-auto rounded-xl bg-background/70 p-1">
            {tabs.map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`rounded-lg px-4 py-2 text-sm font-bold transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === tab
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground hover:bg-accent"
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
          <div className="relative flex-1 sm:ml-auto sm:max-w-sm">
            <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground text-sm" />
            <input
              type="text"
              placeholder="Search player..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex h-11 w-full rounded-xl border border-border bg-background/70 py-2 pl-11 pr-4 text-sm placeholder:text-muted-foreground focus:border-ring focus:outline-none focus:ring-2 focus:ring-ring/40"
            />
          </div>
        </div>

        {/* Table */}
        <div className="overflow-hidden rounded-3xl border border-border bg-card/85 shadow-2xl shadow-black/20">
          {/* Header */}
          <div className="flex items-center border-b border-border/60 bg-background/60 px-4 py-4">
            <div className="w-10 shrink-0 text-xs font-semibold text-muted-foreground uppercase tracking-wide text-center">#</div>
            <div className="flex-1 min-w-0 text-xs font-semibold text-muted-foreground uppercase tracking-wide pl-2">Player</div>
            <div className="w-32 shrink-0 text-xs font-semibold text-muted-foreground uppercase tracking-wide text-right">Money</div>
            <div className="hidden md:block w-24 shrink-0 text-xs font-semibold text-muted-foreground uppercase tracking-wide text-right">Shards</div>
            <div className="hidden sm:block w-24 shrink-0 text-xs font-semibold text-muted-foreground uppercase tracking-wide text-right">Kills</div>
            <div className="hidden sm:block w-24 shrink-0 text-xs font-semibold text-muted-foreground uppercase tracking-wide text-right">Deaths</div>
            <div className="hidden sm:block w-24 shrink-0 text-xs font-semibold text-muted-foreground uppercase tracking-wide text-right">XP</div>
            <div className="hidden sm:block w-24 shrink-0 text-xs font-semibold text-muted-foreground uppercase tracking-wide text-right">Time</div>
          </div>

          {/* Rows */}
          {paginated.map((player, idx) => {
            const icon = rankBadge(player.rank);
            const rankStyle = getLuckPermsStyle(player.luckpermsGroup, player.luckpermsColor);
            return (
              <div
                key={player.username}
                onClick={() => setSelectedPlayer(player)}
                className={`flex items-center px-4 py-4 transition-colors hover:bg-accent/70 cursor-pointer ${
                  idx !== paginated.length - 1 ? "border-b border-border/30" : ""
                } ${player.rank <= 3 ? "bg-primary/[0.045]" : ""}`}
              >
                {/* Rank */}
                <div className="w-10 shrink-0 flex items-center justify-center">
                  {icon ? (
                    <i className={`${icon} text-lg`} />
                  ) : (
                    <span className="rounded-lg bg-background/80 px-2 py-1 text-sm font-bold text-muted-foreground">{player.rank}</span>
                  )}
                </div>

                {/* Player */}
                <div className="flex-1 min-w-0 flex items-center gap-3 pl-2">
                  <div className="relative shrink-0">
                    <img
                      src={`https://mc-heads.net/avatar/${player.username}/32`}
                      alt={player.username}
                      className="h-10 w-10 rounded-xl object-cover bg-primary/10 ring-1 ring-border"
                      onError={(e) => {
                        (e.currentTarget as HTMLImageElement).src =
                          "https://mc-heads.net/avatar/MHF_Steve/32";
                      }}
                    />
                    <span
                      className={`absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border border-card ${
                        player.online ? "bg-green-500" : "bg-gray-500/60"
                      }`}
                    />
                  </div>
                  <div className="flex flex-col min-w-0">
                    <span className="font-black text-sm truncate">{player.username}</span>
                    <span
                      className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full w-fit leading-none mt-0.5 ${
                        rankStyle.hexColor ? "" : `${rankStyle.textClass} ${rankStyle.bgClass}`
                      }`}
                      style={
                        rankStyle.hexColor
                          ? { color: rankStyle.hexColor, backgroundColor: `${rankStyle.hexColor}22` }
                          : undefined
                      }
                    >
                      {rankStyle.label}
                    </span>
                  </div>
                </div>

                {/* Money */}
                <div className="w-32 shrink-0 text-right relative group">
                  <span className="text-sm font-semibold text-green-400 cursor-default">
                    {formatLeaderboardMoney(player.score)}
                  </span>
                  <div className="pointer-events-none absolute bottom-full right-0 mb-1.5 z-20 rounded-md border border-border bg-popover px-2.5 py-1.5 text-xs font-mono text-foreground whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                    ${player.score.toLocaleString()}
                  </div>
                </div>

                {/* Shards */}
                <div className="hidden md:block w-24 shrink-0 text-right">
                  <span className="text-sm font-semibold text-purple-400">{formatCompact(player.shards || 0)}</span>
                </div>

                {/* Kills */}
                <div className="hidden sm:block w-24 shrink-0 text-right">
                  <span className="text-sm font-semibold text-red-400">{formatCompact(player.kills)}</span>
                </div>

                {/* Deaths */}
                <div className="hidden sm:block w-24 shrink-0 text-right">
                  <span className="text-sm font-semibold text-orange-400">{formatCompact(player.deaths)}</span>
                </div>

                {/* XP */}
                <div className="hidden sm:block w-24 shrink-0 text-right">
                  <span className="text-sm font-semibold text-sky-300">{formatCompact(player.xp || 0)}</span>
                </div>

                {/* Playtime */}
                <div className="hidden sm:block w-24 shrink-0 text-right">
                  <span className="text-xs font-semibold text-yellow-400 font-mono">{player.playtime}</span>
                </div>
              </div>
            );
          })}

          {filtered.length === 0 && (
            <div className="py-16 text-center text-muted-foreground text-sm">
              <i className="ri-user-search-line text-3xl mb-3 block" />
              No players found matching &quot;{search}&quot;
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-border/30">
              <span className="text-xs text-muted-foreground">
                Showing {(page - 1) * PAGE_SIZE + 1}-{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length} players
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-1 text-xs rounded-md border border-border hover:bg-accent/60 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Previous
                </button>
                <span className="text-xs text-muted-foreground">{page} / {totalPages}</span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-3 py-1 text-xs rounded-md border border-border hover:bg-accent/60 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>

        <p className="text-xs text-muted-foreground/60 mt-3 text-center">
          Survival Multiplayer &middot; applesmp.us
        </p>
      </main>
      <Footer />

      <PlayerProfileModal
        player={selectedPlayer}
        onClose={() => setSelectedPlayer(null)}
      />
    </div>
  );
}
